import React from "react";
import { Box, Typography, Card, CardContent, Button } from "@mui/material";
import ArticleIcon from '@mui/icons-material/Article';
import LaunchIcon from '@mui/icons-material/Launch';
import "./FeaturedWork.scss";

const FeaturedWork = () => {
    return (
        <Box className="featured-work-section" id="featured-work" sx={{ 
            width: '100%', 
            py: 8,
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
            fontFamily: 'Inter, sans-serif'
        }}>
            <Box sx={{ 
                width: '100%', 
                maxWidth: 1200, 
                mx: 'auto', 
                px: { xs: 2, md: 4 }
            }}>
                {/* Section Header */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="h3" sx={{ 
                        color: '#64ffda', 
                        fontWeight: 700, 
                        mb: 2,
                        fontSize: { xs: '2rem', md: '3rem' }
                    }}>
                        Featured Work
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: '#b0b0b0', 
                        fontWeight: 400,
                        fontSize: { xs: '1rem', md: '1.2rem' }
                    }}>
                        Recent achievements and publications
                    </Typography>
                </Box>

                {/* Featured Work Cards */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', lg: 'row' }, 
                    gap: 4,
                    alignItems: 'center'
                }}>
                    {/* Product Hunt Card */}
                    <Box sx={{ flex: 1, width: '100%', maxWidth: 600 }}>
                        <Card sx={{ 
                            bgcolor: 'rgba(45,45,45,0.8)', 
                            backdropFilter: 'blur(16px)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 3,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                borderColor: 'rgba(100, 255, 218, 0.3)',
                                boxShadow: '0 16px 48px rgba(100, 255, 218, 0.15)',
                                transform: 'translateY(-4px)'
                            }
                        }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h5" sx={{ 
                                    color: '#64ffda', 
                                    mb: 3, 
                                    fontWeight: 600,
                                    fontSize: { xs: '1.3rem', md: '1.5rem' }
                                }}>
                                    Featured on Product Hunt
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                                }}>
                                    <iframe 
                                        style={{ border: 'none' }} 
                                        src="https://cards.producthunt.com/cards/products/1091527" 
                                        width="500" 
                                        height="405" 
                                        frameBorder="0" 
                                        scrolling="no" 
                                        allowFullScreen
                                        title="Product Hunt Card"
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Medium Article */}
                    <Box sx={{ flex: 1, width: '100%', maxWidth: 600 }}>
                        <Card sx={{ 
                            bgcolor: 'rgba(45,45,45,0.8)', 
                            backdropFilter: 'blur(16px)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 3,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            height: '100%',
                            '&:hover': {
                                borderColor: 'rgba(100, 255, 218, 0.3)',
                                boxShadow: '0 16px 48px rgba(100, 255, 218, 0.15)',
                                transform: 'translateY(-4px)'
                            }
                        }}>
                            <CardContent sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'center'
                            }}>
                                <Typography variant="h5" sx={{ 
                                    color: '#64ffda', 
                                    mb: 3, 
                                    fontWeight: 600,
                                    fontSize: { xs: '1.3rem', md: '1.5rem' }
                                }}>
                                    Latest Article
                                </Typography>
                                <Typography variant="h6" sx={{ 
                                    color: '#b0b0b0', 
                                    mb: 3, 
                                    lineHeight: 1.6,
                                    fontWeight: 500,
                                    fontSize: { xs: '1.1rem', md: '1.2rem' }
                                }}>
                                    "Medicare: Bridging the Gap Between People and Healthcare with AI"
                                </Typography>
                                <Typography variant="body1" sx={{ 
                                    color: '#888888', 
                                    mb: 4, 
                                    fontStyle: 'italic',
                                    lineHeight: 1.6,
                                    fontSize: { xs: '0.9rem', md: '1rem' }
                                }}>
                                    Exploring how AI can revolutionize healthcare accessibility and improve patient outcomes through innovative technology solutions.
                                </Typography>
                                <Button
                                    variant="contained"
                                    href="https://medium.com/@ml-assasn/medicare-bridging-the-gap-between-people-and-healthcare-with-ai-2ba0490ae9e6"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<ArticleIcon />}
                                    endIcon={<LaunchIcon />}
                                    sx={{
                                        background: 'linear-gradient(135deg, #64ffda, #00d4ff)',
                                        color: '#0a0a0a',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1.5,
                                        fontSize: { xs: '0.95rem', md: '1rem' },
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #00d4ff, #64ffda)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 24px rgba(100, 255, 218, 0.3)'
                                        }
                                    }}
                                >
                                    Read Article
                                </Button>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default FeaturedWork; 